import { useEffect, useRef, useState, type ReactNode } from 'react';
import { OrderPdfPreview } from '@/pages/oms/orders/[id]/OrderPdfPreview';
import { deleoLabelMoreNote } from '@/utils/fulfillmentDeleoLabel';
import type { LabelBundle, PackingInvoice } from '@/infrastructure/FulfillmentAdminClient';
import {
  packingNotPrintedNote,
  readParcels,
  sheetOutcomesOf,
  type Parcel,
  type ParcelPrintOutcome,
} from '@/pages/fulfillment/components/parcelPaperwork';
import { NO_CARRIER_LABEL, noWaybillCount, noWaybillNotice, type CarrierLabelBatch } from '../carrierLabel';
import { formatBulkResult, UNCONFIRMED_BULK_RESULT } from '@/pages/fulfillment/components/bulkResult';
import type { FulfillmentToastState } from '@/pages/fulfillment/components/useFulfillmentToast';
import { useModalDialog } from '@/pages/fulfillment/components/useModalDialog';
import { PrintFailureNotice } from '@/pages/fulfillment/components/PrintDispatcher';
import type { PrintOutcome } from '@/models/FulfillmentPrint';

export interface CarrierLabelModalProps {
  batch: CarrierLabelBatch;
  onClose: () => void;
  /** Reads archived labels back as one document; the page owns the client, this sheet only shows it. */
  onBundle: (orderIds: number[]) => Promise<LabelBundle>;
  /** The PACKING sheet behind one parcel's label, or null when it could not be read and the label prints alone. */
  onPackingInvoice: (orderId: number) => Promise<PackingInvoice | null>;
  /**
   * The page owns the printer; this sheet hands it the click's whole paperwork as one list — each archived
   * parcel, then each drawn one, every one with its PACKING sheet.
   */
  onPrintAll: (parcels: Parcel[]) => Promise<ParcelPrintOutcome[]>;
  /** Holds the page's printer for the whole run, reads included, so a sheet closed mid-print still blocks another Print. */
  holdPrinter: (run: () => Promise<boolean>) => Promise<boolean>;
  onResult: (outcome: FulfillmentToastState) => void;
  /** Native modal dialogs cover page-level hosts; keep the single shared host in the active surface. */
  notifications?: ReactNode;
}

/** One line of the sheet, tagged by who owns the waybill so the cell has no shape to guess at. */
type LabelRow =
  | { kind: 'archived'; orderId: number; tracking: string; url: string }
  | { kind: 'drawn'; orderId: number; tracking: string }
  | { kind: 'missing'; orderId: number; tracking: null; reason: string };

/** Split by label source: archived label URLs can go stale, while the Admin draws Deleo-family stickers here. */
export function CarrierLabelModal({
  batch,
  onClose,
  onBundle,
  onPackingInvoice,
  onPrintAll,
  holdPrinter,
  onResult,
  notifications,
}: CarrierLabelModalProps) {
  /** The stickers this desk draws, rendered off screen so the document can read their markup. */
  const deleoLabelsRef = useRef<HTMLDivElement>(null);
  const dialog = useModalDialog(onClose);

  const [bundling, setBundling] = useState(false);
  const runningRef = useRef(false);
  // Its own effect rather than a cleanup on the print one: StrictMode re-runs effects, and the print one returns early.
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);
  const [shortBy, setShortBy] = useState<number[] | null>(null);
  const [bundleError, setBundleError] = useState<string | null>(null);

  const reportUnknown = () => {
    setBundleError(UNCONFIRMED_BULK_RESULT);
    onResult({ tone: 'error', text: UNCONFIRMED_BULK_RESULT });
  };

  /**
   * One toast for the click: orders whose label went, orders the archive had nothing for, parcels the document
   * could not take or a printer refused, and PACKING sheets that could not go — named, so the packer prints
   * them from View Orders.
   */
  const reportAll = (
    results: ParcelPrintOutcome[],
    skipped: number[],
    failedReads: Array<{ orderId: number; reason: string }>,
  ) => {
    const refusal = (outcome: PrintOutcome) => (outcome.ok ? null : PrintFailureNotice.textOf(outcome));
    const failed = [
      ...failedReads.map(({ orderId, reason }) => ({ item: `Order ${orderId}`, reason })),
      ...results.flatMap((parcel) => {
        const label = refusal(parcel.label);
        const packing = parcel.packing ? refusal(parcel.packing) : null;
        return [
          ...(label ? [{ item: `Order ${parcel.orderId}`, reason: label }] : []),
          ...(packing ? [{ item: `Order ${parcel.orderId} PACKING`, reason: packing }] : []),
        ];
      }),
    ];
    const printed = results.filter((parcel) => parcel.label.ok);
    // A sheet refused with its own words is already named above; this names the ones with no invoice to draw from.
    const withoutSheet = printed
      .filter((parcel) => !parcel.sheetSent && !parcel.packing)
      .map((parcel) => parcel.orderId);
    const submitted =
      printed.length > 0
        ? ['Print job submitted.', packingNotPrintedNote(withoutSheet, 'View Orders')].filter(Boolean).join(' ')
        : undefined;
    onResult(
      formatBulkResult(
        'Print Labels',
        printed.length,
        skipped.map((orderId) => ({ item: `Order ${orderId}`, reason: NO_CARRIER_LABEL })),
        failed,
        submitted,
      ),
    );
  };

  /** The hidden sheet's Deleo-family sticker — print area only — as markup the document can take; empty when absent. */
  const waybillOf = (orderId: number): string =>
    deleoLabelsRef.current?.querySelector(`[data-waybill="${orderId}"] .print-area`)?.outerHTML ?? '';

  /**
   * The click's whole paperwork as one document: every archived parcel's label with its PACKING sheets behind
   * it, then each drawn sticker with its own — one button, one print, one pdf [SSD-7207, 10/13]. Each label is
   * read on its own so its sheets can follow it; a label the archive cannot read is named and the rest still print.
   */
  const printAll = async (): Promise<boolean> => {
    if (runningRef.current) {
      return false;
    }
    runningRef.current = true;
    setBundling(true);
    setBundleError(null);
    return holdPrinter(async () => {
      try {
        const { parcels, skipped, failed } = await readParcels(batch.labels, onBundle, onPackingInvoice);
        setShortBy(skipped.length > 0 ? skipped : null);
        const drawn: Parcel[] = batch.drawn.map((row) => ({
          orderId: row.orderId,
          trackingNumber: row.trackingNumber,
          waybill: waybillOf(row.orderId),
          invoice: row.invoice ?? null,
        }));
        const all: Parcel[] = [...parcels, ...drawn];
        if (all.length === 0) {
          onResult(
            formatBulkResult(
              'Print Labels',
              0,
              skipped.map((orderId) => ({ item: `Order ${orderId}`, reason: NO_CARRIER_LABEL })),
              failed.map(({ orderId, reason }) => ({ item: `Order ${orderId}`, reason })),
            ),
          );
          return false;
        }
        const results = await onPrintAll(all);
        reportAll(results, skipped, failed);
        return skipped.length === 0 && failed.length === 0 && sheetOutcomesOf(results).every((outcome) => outcome.ok);
      } catch {
        reportUnknown();
        return false;
      } finally {
        runningRef.current = false;
        setBundling(false);
      }
    });
  };

  // Opened by a Print click, the sheet prints straight away; waiting for a second press read as nothing printing.
  // A clean run closes the sheet; a run that left an order out stays open, so Print can still reach it [SSD-7207, 10/14].
  const printedNowRef = useRef(false);
  useEffect(() => {
    if (printedNowRef.current) {
      return;
    }
    printedNowRef.current = true;
    // A sheet of rows with nothing to print says so on its own.
    if (batch.labels.length + batch.drawn.length === 0) {
      return;
    }
    const leftOut = batch.missing.length + (batch.failed ?? []).length > 0;
    void printAll().then((clean) => {
      // A sheet the desk already closed must not close the one a newer print opened in its place.
      if (clean && !leftOut && mountedRef.current) {
        onClose();
      }
    });
    // Once, on open: the click that opened the sheet is the one that prints.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const rows: LabelRow[] = [
    ...batch.labels.map((label) => ({
      kind: 'archived' as const,
      orderId: label.orderId,
      tracking: label.trackingNumber,
      url: label.url,
    })),
    ...batch.drawn.map((drawn) => ({ kind: 'drawn' as const, orderId: drawn.orderId, tracking: drawn.trackingNumber })),
    ...[...batch.missing, ...(batch.failed ?? [])].map((miss) => ({
      kind: 'missing' as const,
      orderId: miss.orderId,
      tracking: null,
      reason: miss.reason,
    })),
  ];
  const resolved = batch.labels.length + batch.drawn.length;
  const withoutWaybill = noWaybillCount(batch);

  return (
    <>
      {batch.drawn.length > 0 && (
        <div style={{ position: 'absolute', left: '-9999px', top: '-9999px', visibility: 'hidden' }} aria-hidden="true">
          <div ref={deleoLabelsRef}>
            {batch.drawn.map((drawn) => (
              // The sticker is drawn here so the document can read its markup; the PACKING sheet is drawn by the document.
              <div key={drawn.orderId} data-waybill={drawn.orderId}>
                <OrderPdfPreview order={drawn.order} moreNote={deleoLabelMoreNote(drawn.order)} />
              </div>
            ))}
          </div>
        </div>
      )}
      <dialog
        ref={dialog}
        className="rto-modal carrier-label-modal"
        aria-label="Carrier labels"
        onClick={(event) => event.target === event.currentTarget && onClose()}
      >
        <header>
          Carrier labels ({resolved} of {rows.length} orders)
          <button type="button" className="x" aria-label="Close labels" onClick={onClose}>
            ✕
          </button>
        </header>
        <div className="body">
          {/* Said before the rows, so the count on the print button is not read as the whole selection. */}
          {withoutWaybill > 0 && (
            <div className="blocked-note" role="note">
              {noWaybillNotice(withoutWaybill)}
            </div>
          )}
          <table className="picktbl">
            <thead>
              <tr>
                <th>Order</th>
                <th>Parcel</th>
                <th>Label</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.orderId}>
                  <td className="num">{row.orderId}</td>
                  <td className="num">{row.tracking ?? '—'}</td>
                  <td>
                    {row.kind === 'archived' ? (
                      <a href={row.url} target="_blank" rel="noopener noreferrer">
                        Open label
                      </a>
                    ) : row.kind === 'drawn' ? (
                      <span>Drawn here (Deleo)</span>
                    ) : (
                      <span className="blocked-note">{row.reason}</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {shortBy && (
            <div className="blocked-note">The sheet came up short — no archived label for {shortBy.join(', ')}.</div>
          )}
          {bundleError && <div className="blocked-note">{bundleError}</div>}
          <div className="note">
            Most parcels wear the carrier&apos;s own waybill — barcode, number and service all come from them — and each
            link opens their pdf to view. Print sends the whole list as one document: every label with its PACKING sheet
            behind it. Deleo publishes no label endpoint, so its sticker is drawn here and goes into that same document.
          </div>
        </div>
        <div className="carrier-label-notifications">{notifications}</div>
        <div className="foot">
          <button type="button" className="btn btn-gray btn-sm" onClick={onClose}>
            Close
          </button>
          <button
            type="button"
            className="btn btn-blue btn-sm"
            disabled={resolved === 0 || bundling}
            onClick={() => void printAll()}
          >
            Print {resolved} label{resolved === 1 ? '' : 's'}
          </button>
        </div>
      </dialog>
    </>
  );
}
